export const flipMenu = (heroMenu, placeholder) => {
    // menu.style.visibility = "hidden"; // don't use display: none
    placeholder.appendChild(heroMenu);
};

/**
 * Creates a status and time helper for an element
 * @param {HTMLElement|string} element - The element or selector to apply status and time to
 * @param {Object} options - Configuration options
 * @param {boolean} options.showTime - Whether to show time (default: true)
 * @param {boolean} options.flashSeconds - Whether to flash seconds indicator (default: true)
 * @param {string} options.onlineClass - CSS class for online status (default: 'online')
 * @param {string} options.offlineClass - CSS class for offline status (default: 'offline')
 * @param {string} options.timeFormat - Time format: '12h' or '24h' (default: '24h')
 * @param {boolean} options.optimized - Use optimized updates (default: true)
 */
export function createStatusTimeHelper(element, options = {}) {
    // Default options
    const config = {
        showTime: true,
        flashSeconds: true,
        onlineClass: 'online',
        offlineClass: 'offline',
        timeFormat: '24h',
        optimized: true,
        ...options
    };
    
    // Get element if string selector provided
    const el = typeof element === 'string' ? document.querySelector(element) : element;
    
    if (!el) {
        console.error('Element not found');
        return null;
    }
    
    let timeInterval = null;
    let flashInterval = null;
    let isFlashing = false;
    let lastTimeString = '';
    let hoursSpan, minutesSpan, indicatorSpan, ampmSpan;
    
    // Create DOM structure once
    function createTimeStructure() {
        if (!config.optimized) return;
        
        hoursSpan = document.createElement('span');
        indicatorSpan = document.createElement('span');
        indicatorSpan.className = 'ht-indicator';
        minutesSpan = document.createElement('span');
        
        el.appendChild(hoursSpan);
        el.appendChild(indicatorSpan);
        el.appendChild(minutesSpan);
        
        if (config.timeFormat === '12h') {
            ampmSpan = document.createElement('span');
            el.appendChild(document.createTextNode(' '));
            el.appendChild(ampmSpan);
        }
    }
    
    // Update online/offline status
    function updateStatus() {
        el.classList.remove(config.onlineClass, config.offlineClass);
        el.classList.add(navigator.onLine ? config.onlineClass : config.offlineClass);
    }
    
    // Optimized time update - only update changed parts
    function updateTimeOptimized() {
        const now = new Date();
        let hours = now.getHours();
        const minutes = now.getMinutes().toString().padStart(2, '0');
        
        let hoursStr, ampm = '';
        
        if (config.timeFormat === '12h') {
            ampm = hours >= 12 ? 'PM' : 'AM';
            hours = hours % 12;
            hours = hours ? hours : 12;
            hoursStr = hours.toString().padStart(2, '0');
        } else {
            hoursStr = hours.toString().padStart(2, '0');
        }
        
        // Only update if hours changed
        if (hoursSpan.textContent !== hoursStr) {
            hoursSpan.textContent = hoursStr;
        }
        
        // Only update if minutes changed
        if (minutesSpan.textContent !== minutes) {
            minutesSpan.textContent = minutes;
        }
        
        // Update AM/PM only if changed
        if (ampmSpan && ampmSpan.textContent !== ampm) {
            ampmSpan.textContent = ampm;
        }
        
        // Handle colon flashing
        const colonDisplay = (config.flashSeconds && isFlashing) ? '' : ':';
        if (indicatorSpan.textContent !== colonDisplay) {
            indicatorSpan.textContent = colonDisplay;
        }
    }
    
    // Fallback: Traditional update method
    function updateTime() {
        if (!config.showTime) return;
        
        if (config.optimized && hoursSpan) {
            updateTimeOptimized();
            return;
        }
        
        const now = new Date();
        let hours = now.getHours();
        const minutes = now.getMinutes().toString().padStart(2, '0');
        
        let newTimeString;
        
        if (config.timeFormat === '12h') {
            const ampm = hours >= 12 ? 'PM' : 'AM';
            hours = hours % 12;
            hours = hours ? hours : 12;
            hours = hours.toString().padStart(2, '0');
            
            const colonDisplay = (config.flashSeconds && isFlashing) ? '' : ':';
            newTimeString = `<span>${hours}</span><span class="ht-indicator">${colonDisplay}</span><span>${minutes}</span> <span>${ampm}</span>`;
        } else {
            hours = hours.toString().padStart(2, '0');
            const colonDisplay = (config.flashSeconds && isFlashing) ? '' : ':';
            newTimeString = `<span>${hours}</span><span class="ht-indicator">${colonDisplay}</span><span>${minutes}</span>`;
        }
        
        // Only update DOM if content actually changed
        if (lastTimeString !== newTimeString) {
            el.innerHTML = newTimeString;
            lastTimeString = newTimeString;
        }
    }
    
    // Use requestAnimationFrame for smoother updates
    function scheduleUpdate() {
        if (config.optimized) {
            requestAnimationFrame(updateTime);
        } else {
            updateTime();
        }
    }
    
    // Start the helper
    function start() {
        // Initial status update
        updateStatus();
        
        // Listen for online/offline events
        window.addEventListener('online', updateStatus);
        window.addEventListener('offline', updateStatus);
        
        if (config.showTime) {
            // Create optimized DOM structure
            if (config.optimized) {
                createTimeStructure();
            }
            
            // Initial time update
            scheduleUpdate();
            
            // Update time every second using more efficient method
            timeInterval = setInterval(scheduleUpdate, 1000);
            
            // Flash colon indicator
            if (config.flashSeconds) {
                flashInterval = setInterval(() => {
                    isFlashing = !isFlashing;
                    scheduleUpdate();
                }, 500);
            }
        }
    }
    
    // Stop the helper and clean up
    function stop() {
        window.removeEventListener('online', updateStatus);
        window.removeEventListener('offline', updateStatus);
        
        if (timeInterval) {
            clearInterval(timeInterval);
            timeInterval = null;
        }
        
        if (flashInterval) {
            clearInterval(flashInterval);
            flashInterval = null;
        }
    }
    
    // Manual update methods
    function updateNow() {
        updateStatus();
        if (config.showTime) updateTime();
    }
    
    // Return control object
    return {
        start,
        stop,
        updateNow,
        element: el,
        config
    };
}

export function numberWithCommas(x) {
  return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}